import React, { useState, useEffect } from 'react';

import { Popover, Menu, Badge } from 'antd';
import _ from 'lodash';

import ls from 'local-storage';
import styled from 'styled-components';

import { getContactName, isContactApproved } from '@hooks/API/Contact/UseContactsList';
import {
    useActivePackages,
    useAddPlaylistToPackage,
    useDeletePlaylistFromActivePackage,
    usePackage,
    useAddPackage,
    forceReloadActivePackages,
} from '@hooks/API/Packages/UsePackages';

import AddPackageDrawer from '@components/ActivePackages/AddPackageDrawer';
import NwIcon from '@components/Gui/NwIcon';
import PackageDrawer from '@components/PackageDrawer/PackageDrawer';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const Wrapper = styled.div`
    .ant-dropdown-menu-item-group-list {
        max-height: unset;
    }

    button {
        text-transform: lowercase;
        font-size: 0.8rem;
        margin-top: 4px;
    }
`;

const Content = styled.div`
    padding-bottom: 1rem;
    min-width: 20rem;

    .error-message {
        padding: 1rem 2rem;
        text-align: center;
        color: ${(props) => props.theme.DangerColor};
        text-transform: uppercase;
        max-width: 20rem;
    }

    .ant-menu-vertical {
        border-right: none;
    }

    .ant-menu {
        border-radius: 0 0 6px 6px;
    }
    .ant-menu-item {
        margin: 0 !important;
        border-bottom: ${(props) => props.theme.DefaultBorder};
        height: 2rem;
        line-height: 2rem;

        &:last-child {
            border-bottom: none;
        }

        text-transform: uppercase;
        font-size: 0.8rem;
        vertical-align: middle;

        svg {
            margin: 0 6px;
        }

        .active {
            font-weight: bold;

            svg {
                color: ${(props) => props.theme.DangerColor};
            }
        }

        .badge {
            color: #fff;
            background-color: ${(props) => props.theme.DangerColor};
            display: inline-block;
            padding: 0.25em 0.4em;
            font-size: 0.75rem;
            font-weight: 700;
            line-height: 1;
            text-align: center;
            white-space: nowrap;
            margin-top: 0.2em;
            vertical-align: baseline;
            border-radius: 0.25rem;
            transition:
                color 0.15s ease-in-out,
                background-color 0.15s ease-in-out,
                border-color 0.15s ease-in-out,
                box-shadow 0.15s ease-in-out;
        }
    }
`;

const StyledBadge = styled.div`
    /* antd applica reset-component() a .ant-badge, che riporta il font-size a
       @font-size-base: senza inherit l'icona non segue la size del contenitore. */
    .ant-badge {
        font-size: inherit;
    }

    /* antd ancora il contatore all'angolo in alto a destra del box icona
       (top/right 0 + translate 50%). Lo scostamento va espresso in percentuale
       di quel box, non in px, per restare proporzionato al variare della size. */
    .ant-badge-count {
        background: ${(props) => props.theme.PrimaryColor};
        top: 10%;
        right: 10%;
    }
    .ant-badge-multiple-words {
        padding: 0 3px;
    }
`;

const AddBookToPackageButton = ({ book, children, placement = 'top' }) => {
    const { data: packages } = useActivePackages();
    const [menuActions, setMenuActions] = useState([]);
    const latestPackageId = ls.get('nw_config_latest_package');
    const { mutateAsync: onAddPlaylistToPackage } = useAddPlaylistToPackage();
    const { mutateAsync: onDeletePlaylistFromActivePackage } = useDeletePlaylistFromActivePackage();
    const { mutateAsync: onAddPackage } = useAddPackage();
    const [isAddNewPackage, setIsAddNewPackage] = useState(false);
    const [newPackageId, setNewPackageId] = useState();
    const [isInPackagesCount, setIsInPackagesCount] = useState(0);
    const [isSingleSend, setIsSingleSend] = useState(false);
    const [open, setOpen] = useState(false);

    const isModelApproved = isContactApproved(book.ContactID);

    const getIsAddedBook = (packId) => {
        const pack = packages.find((pack) => pack.ID === packId);
        if (pack && pack.Models) {
            const model = pack.Models.find((model) =>
                model.Playlists.find((playlist) => playlist.PlaylistID === book.ID)
            );
            return model && model.Playlists.find((playlist) => playlist.PlaylistID === book.ID);
        }
        return null;
    };

    const handleAddNewPackage = () => {
        setIsAddNewPackage(true);
    };

    const addToPackage = async (selectedPackageId) => {
        const data = {
            packageID: selectedPackageId,
            playlistID: book.ID,
        };
        try {
            await onAddPlaylistToPackage({
                data,
                isActivePackage: packages.find((pack) => pack.ID === selectedPackageId),
            });
            forceReloadActivePackages();
        } catch (error) {
            console.error('Add Playlist To Package Error => ', error);
        }
    };

    const removeFromPackage = async (selectedPackageId, packagePlaylistId) => {
        try {
            await onDeletePlaylistFromActivePackage({
                packageID: selectedPackageId,
                packagePlaylistId: packagePlaylistId,
                isActivePackage: packages.find((pack) => pack.ID === selectedPackageId),
            });
            forceReloadActivePackages();
        } catch (error) {
            console.error('Delete Playlist To Package Error => ', error);
        }
    };

    const addPackage = async () => {
        const data = {
            Name: getContactName(book.ContactID),
            Models: [
                {
                    Playlists: [
                        {
                            PlaylistID: book.ID,
                            Note: book.Name,
                        },
                    ],
                    ModelID: book.ContactID,
                },
            ],
        };
        try {
            const res = await onAddPackage({ data });
            if (res) {
                setIsSingleSend(true);
                setNewPackageId(res.ID);
            }
        } catch (error) {
            console.error('Add Package Error => ', error);
        }
    };

    useEffect(() => {
        if (packages) {
            let actions = [];
            const packages_enriched = packages.map((p) => ({
                ...p,
                isAdded: getIsAddedBook(p.ID),
            }));
            const packagesWith = packages_enriched.filter((p) => {
                return p.isAdded;
            });
            const packagesWithout = _.difference(packages_enriched, packagesWith);
            const newpackageaction = {
                name: 'create a new package',
                key: 'create-0',
                icon: byPrefixAndName.fasl['book-arrow-right'],
                added: false,
            };
            if (packagesWithout.length) {
                const sortedPackagesWithout = packagesWithout.sort(function (x, y) {
                    return x.ID === latestPackageId ? -1 : y.ID === latestPackageId ? 1 : 0;
                });
                const subactions = sortedPackagesWithout.map((p) => ({
                    name: `${p.Name}${p.ID === latestPackageId ? ' [last used]' : ''}`,
                    key: `add-${p.ID}`,
                    icon: byPrefixAndName.fasl['plus-square'],
                    added: false,
                }));
                actions.push({
                    group: true,
                    name: 'add to a package',
                    subactions: [newpackageaction, ...subactions],
                });
            } else {
                actions.push({
                    group: true,
                    name: 'add to a package',
                    subactions: [newpackageaction],
                });
            }
            if (packagesWith.length) {
                const removesubactions = packagesWith.map((p) => ({
                    name: p.Name,
                    key: `remove-${p.ID}-${p.isAdded.ID}`,
                    icon: byPrefixAndName.fasl['minus-square'],
                    added: true,
                }));
                actions.push({
                    group: true,
                    name: 'remove from package',
                    subactions: [...removesubactions],
                });
            }
            setIsInPackagesCount(packagesWith.length);

            actions.push({
                group: true,
                name: 'quick send',
                subactions: [
                    {
                        name: 'send as a single book',
                        key: 'send-0',
                        icon: byPrefixAndName.fasl['book-circle-arrow-right'],
                        added: false,
                    },
                ],
            });
            setMenuActions(actions);
        }
    }, [packages]);

    const onClick = (e) => {
        const action = e.key.split('-')[0];
        const clickedPackageId = Number(e.key.split('-')[1]);
        const packagePlaylistId = action === 'remove' && Number(e.key.split('-')[2]);

        switch (action) {
            case 'create':
                handleAddNewPackage();
                break;
            case 'add':
                addToPackage(clickedPackageId);
                break;
            case 'remove':
                removeFromPackage(clickedPackageId, packagePlaylistId);
                break;
            case 'send':
                addPackage();
                break;
            default:
                break;
        }
        setOpen(false);
    };

    const renderAction = (action, index) => {
        if (action.group) {
            return {
                type: 'group',
                key: ` group-${index}`,
                label: action.name,
                children: action.subactions.map(renderAction),
            };
        }
        return {
            key: action.key,
            label: (
                <span>
                    <NwIcon icon={action.icon} />
                    {action.name}
                </span>
            ),
        };
    };

    const handleOpenPackage = (selectedPackageId) => {
        setNewPackageId(selectedPackageId);
    };

    const handleClosePackageDrawer = () => {
        setNewPackageId();
        setIsSingleSend(false);
    };

    return (
        <Wrapper>
            <Popover
                zIndex={9999}
                overlayClassName="folder-popover"
                placement={placement}
                trigger="click"
                open={open}
                onOpenChange={setOpen}
                content={
                    isModelApproved ? (
                        <Content>
                            <Menu
                                onClick={onClick}
                                className="nw-small-context-menu"
                                items={menuActions.map(renderAction)}
                            />
                        </Content>
                    ) : (
                        <Content>
                            <div className="error-message">
                                This model is pending approval and can't be added to a package.
                            </div>
                        </Content>
                    )
                }
            >
                {children ? (
                    children
                ) : (
                    <StyledBadge>
                        <Badge count={isInPackagesCount} size="small">
                            <NwIcon icon={byPrefixAndName.fasl['book-arrow-right']} size="lg" />
                        </Badge>
                    </StyledBadge>
                )}
                {/* {children} */}
            </Popover>
            {isAddNewPackage && (
                <AddPackageDrawer
                    onAddedPackage={handleOpenPackage}
                    bookIDs={[book.ID]}
                    onClose={() => setIsAddNewPackage(false)}
                />
            )}
            {newPackageId && (
                <PackageDrawer
                    packageId={newPackageId}
                    isSingleSend={isSingleSend}
                    onClose={handleClosePackageDrawer}
                />
            )}
        </Wrapper>
    );
};

export default AddBookToPackageButton;
